import Partition from 'molecules/partition'

import chain from 'assets/home/sustainability/supply-chain 1.png'
import ipm from 'assets/home/sustainability/ipm.png'
import farming from 'assets/home/sustainability/carbon_farming.png'

import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

gsap.registerPlugin(ScrollTrigger)
ScrollTrigger.defaults({})

const touchPoints = [
  {
    title: 'Supply chain traceability',
    desc: 'Ensuring a safe and transparent supply chain that guarantees residue-free produce for our families, peers, and fellow humans.',
    icon: chain,
    target: '/supply-chain',
  },
  {
    title: 'Integrated Pest Management',
    desc: 'Ensuring an ecological and cost-efficient approach to pest management that controls pests with minimal pesticides for residue-free and safe-to-consume produce.',
    icon: ipm,
    target: '/ipm',
  },
  {
    title: 'Carbon farming',
    desc: 'Ensuring continuity in the battle against climate change through farming advisories and practices that increase absorption and storage of carbon in the soil for a healthier planet.',
    icon: farming,
    target: '/carbon-farming',
  },
]

export default function Sustainability() {
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.sustainability-article',
        {
          y: 150,
        },
        {
          duration: 1,
          y: 0,
          ease: 'back.out',
          stagger: 0.1,
          scrollTrigger: {
            trigger: '.sustainability-article-parent',
            start: 'top 80%',
            toggleActions: 'restart none none reverse',
          },
        },
      )
    })

    return () => {
      ctx.revert()
    }
  }, [])

  return (
    <section className="bg-[url('assets/home/sustainability/sustainability_bg.jpg')] bg-cover bg-top">
      <section className="py-16 w-container-lg">
        <section className=" blade-bottom-padding-sm 2xl:blade-bottom-padding-lg">
          <div className="pb-1">
            <Partition text="Sustainability" color="light" />
          </div>
          <div className="grid grid-cols-1 gap-y-4 lg:grid-cols-2 pt-7 ">
            <div>
              <h3 className="font-medium text-cream3">Giving back to nature</h3>
            </div>
            <div>
              <span className="text-sm block lg:text-lg font-light text-cream3 w-full leading-normal max-w-xl xl:max-w-2xl ml-0 lg:ml-auto">
                BharatRohan’s passion for changing the face of agriculture with
                tech is fueled by a sense of sustainability. Every day, we are
                steadily inching towards a more robust and sustainable world
                with our focus on{' '}
                <strong className="font-bold">
                  nine United Nations Sustainable Development Goals (SDGs)
                </strong>
                . Backing us are our partners, clients, and patrons.
              </span>
            </div>
          </div>
        </section>

        <section className="sustainability-article-parent grid sm:grid-cols-2 lg:grid-cols-3 gap-y-12 lg:gap-10 blade-top-padding-sm blade-top-margin-sm">
          {touchPoints.map((elem, index) => {
            const key = `${index}`
            return <Card data={elem} key={key} />
          })}
        </section>
      </section>
    </section>
  )
}

type Props = {
  icon: string
  title: string
  desc: string
  target: string
}
function Card({ data }: { data: Props }) {
  const navigate = useNavigate()

  const { icon, title, desc, target } = data
  return (
    <article className="sustainability-article">
      <div className="flex items-center justify-between gap-3 pb-3 2xl:pb-4 w-11/12 ">
        <div>
          <img className="h-12 xl:h-14 2xl:h-16" src={icon} alt={title} />
        </div>
        <div>
          <button
            onClick={() => {
              navigate(target)
            }}
            type="button"
            aria-label={'Visit' + title + ' page'}
            className="place-content-center cursor-pointer p-3 rounded-full bg-yellow  bg-opacity-100 lg:bg-opacity-[15%] backdrop-blur-sm stroke-white hover:bg-opacity-100 hover:stroke-green focus-visible:bg-green focus-visible:stroke-green outline-none transition-all "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={3}
              stroke="inherit"
              className="w-5 h-5 2xl:h-6 2xl:w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
              />
            </svg>
          </button>
        </div>
      </div>
      <div>
        <h4 className="font-medium text-cream3 pt-3 pb-3">{title}</h4>
        <span className="font-light text-sm md:text-base w-11/12 text-opacity-70 pr-3 block text-cream3 leading-normal">
          {desc}
        </span>
      </div>
    </article>
  )
}
